import React from "react";

import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { trDts } from "@modules/datasheets/utils/i18n";
import { DatasheetFormatType } from "@common/dto/datasheet/type";
import { waitMs } from "@common/utils/mock";
import { useMessage } from "@common/hooks/message";
import { HttpErrorDto } from "@common/dto/http";

import {
  DatasheetFormatManagementConfig,
  DatasheetFormatManagementFormat,
  DatasheetFormatManagementMode,
  DatasheetFormatManagementProps,
  DatasheetFormatManagementSubmitExportParams,
} from "./datasheet-format-management.types";

export const useController = (props: DatasheetFormatManagementProps) => {
  const { onSubmit, getFormats, getFolders, mode, checkFormatNameExists } = props;

  const { closeMessage, showConfirmMessage, messageProps, showErrorMessage } = useMessage();

  const [selectedFormatType, setSelectedFormatType] = React.useState<DatasheetFormatType>(
    DatasheetFormatType.individual
  );
  const [formatName, setFormatName] = React.useState<string>("");
  const [folders, setFolders] = React.useState<Array<FolderPrimaryListDto>>([]);
  const [formats, setFormats] = React.useState<Array<DatasheetFormatManagementFormat>>([]);
  const [selectedFolder, setSelectedFolder] = React.useState<FolderPrimaryListDto | undefined>();
  const [selectedFormat, setSelectedFormat] = React.useState<{ id: number; name: string } | undefined>();

  const [folderPickerOpened, setFolderPickerOpened] = React.useState(false);

  const resetFormats = React.useCallback(() => {
    setFormats([]);
  }, []);

  const openFolderPicker = React.useCallback(() => {
    setFolderPickerOpened(true);
  }, []);

  const closeFolderPicker = React.useCallback(() => {
    setFolderPickerOpened(false);
    resetFormats();
  }, [resetFormats]);

  const handleFormatNameChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormatName(e.target.value);
  }, []);

  const handleSubmit = async () => {
    try {
      const submit = () => {
        onSubmit({
          folder: selectedFolder,
          format: selectedFormat,
        });
        resetFormats();
        setFolderPickerOpened(false);
      };

      if (mode === DatasheetFormatManagementMode.new) {
        const isExists = await checkFormatNameExists();

        if (isExists) {
          return showConfirmMessage({
            content: trDts("wouldYouLikeToOverwriteFormatName"),
            confirmText: trDts("Overwrite"),
            cancelText: trDts("Cancel"),
            onCustomOk: submit,
          });
        }
      }

      submit();
      
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto)?.entity || trDts("Failed to check format name"));
    }
  };

  const getAndSetFormats = React.useCallback(
    async (folderId: number) => {
      const res = await getFormats(folderId);
      setFormats(res);
    },
    [getFormats]
  );

  const getAndSetFolders = React.useCallback(async () => {
    const res = await getFolders();
    setFolders(res);
  }, [getFolders]);

  const deleteFormat = async (folderId: number, formatId: number) => {
    await waitMs(1000);
  };

  const deleteFolder = async (folderId: number) => {
    await waitMs(1000);
  };

  const createFolder = async (data: { parentid?: number; foldername: string }) => {
    await waitMs(1000);
  };

  const handleFormatDelete = React.useCallback(
    async (folderId: number, formatId: number) => {
      await deleteFormat(folderId, formatId);
      await getAndSetFormats(folderId);
    },
    [deleteFormat, getAndSetFolders]
  );

  const handleFolderDelete = React.useCallback(
    async (folderId: number) => {
      await deleteFolder(folderId);
      await getAndSetFolders();
    },
    [deleteFolder, getAndSetFolders]
  );

  const handleFolderCreate = React.useCallback(
    async (data: { parentId?: number; name: string }) => {
      await createFolder({
        parentid: data.parentId,
        foldername: data.name,
      });
      await getAndSetFolders();
    },
    [createFolder, getAndSetFolders]
  );

  const defaultFormatTypes = React.useMemo(
    () => [
      { value: DatasheetFormatType.individual, label: trDts("Individual") },
      { value: DatasheetFormatType.pedigree, label: trDts("Pedigree") },
      { value: DatasheetFormatType.table, label: trDts("Table") },
    ],
    []
  );

  const config: Record<DatasheetFormatManagementMode, DatasheetFormatManagementConfig> = {
    [DatasheetFormatManagementMode.export]: {
      title: trDts("Export Datasheet Format"),
      submitBtnText: trDts("Export"),
      isAllowedToSubmit: (params: {
        folderId?: FolderPrimaryListDto["id"];
        formatId?: DatasheetFormatManagementFormat["id"];
        newFormatName?: string;
      }) => Boolean(params.formatId),
      formatManagement: {
        title: trDts("Export Format"),
        isAllowedSubmit: ({ formatId }: { formatId: number }) => Boolean(formatId),
        submitBtnProps: {
          text: trDts("OK"),
        },
      },
    },
    [DatasheetFormatManagementMode.load]: {
      title: trDts("Load Datasheet Format"),
      submitBtnText: trDts("Load"),
      isAllowedToSubmit: (params: {
        folderId?: FolderPrimaryListDto["id"];
        formatId?: DatasheetFormatManagementFormat["id"];
        newFormatName?: string;
      }) => Boolean(params.formatId),
      formatManagement: {
        title: trDts("Load Format"),
        isAllowedSubmit: ({ formatId }: { formatId: number }) => Boolean(formatId),
        submitBtnProps: {
          text: trDts("Load"),
        },
        deleteFormatBtnProps: {
          text: trDts("Delete"),
        },
        handleFormatDelete,
        handleFolderDelete,
        handleFolderCreate,
      },
    },
    [DatasheetFormatManagementMode.new]: {
      title: trDts("Add New Datasheet"),
      submitBtnText: trDts("Create"),
      isAllowedToSubmit: (params: {
        folderId?: FolderPrimaryListDto["id"];
        formatId?: DatasheetFormatManagementFormat["id"];
        newFormatName?: string;
      }) => Boolean(params.formatId) && Boolean(params.newFormatName?.trim().length),
      showFormatName: true,
      formatManagement: {
        title: trDts("New Format"),
        isAllowedSubmit: ({ formatId }: { formatId: number }) => Boolean(formatId),
        submitBtnProps: {
          text: trDts("OK"),
        },
        deleteFormatBtnProps: {
          text: trDts("Delete"),
        },
        handleFormatDelete,
        handleFolderDelete,
        handleFolderCreate,
      },
    },
  };

  const modeConfig = config[mode];

  const isAllowedToSubmitFormatOrFolderLoader = React.useCallback((params: { folderId: number; formatId?: number }) => {
    return Boolean(params.folderId);
  }, []);

  const handleFolderSelect = React.useCallback(
    async (
      format: DatasheetFormatManagementSubmitExportParams["format"],
      folder: DatasheetFormatManagementSubmitExportParams["folder"]
    ) => {
      setSelectedFolder(folder);
      setSelectedFormat(format);
      closeFolderPicker();
    },
    [closeFolderPicker]
  );

  return {
    defaultFormatTypes,
    selectedFormatType,
    formatName,
    folders,
    formats,
    selectedFolder,
    selectedFormat,
    folderPickerOpened,
    getAndSetFolders,
    handleFolderSelect,
    isAllowedToSubmitFormatOrFolderLoader,
    getAndSetFormats,
    handleSubmit,
    openFolderPicker,
    closeFolderPicker,
    handleFormatNameChange,
    setSelectedFormatType,
    resetFormats,
    modeConfig,
    closeMessage,
    showConfirmMessage,
    messageProps,
  };
};
